import { FilePurpose } from '@/api/enums';
import { File } from '@/client/files';
import { AppButton } from '@/ui/buttons';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FlexContainer } from '../styled-ui';
import { MediaUploadModal } from './MediaUploadModal';

const StyledUploadWrapper = styled(FlexContainer)`
  justify-content: flex-start;
  width: max-content;
  text-align: start;
  padding: var(--small-padding) 0;
  word-break: break-word;

  .media-img {
    max-width: 182px;
    max-height: 132px;
  }
`;

const StyledThumbnail = styled(FlexContainer)`
  overflow: hidden;
  border-radius: 4px;

  img {
    max-width: 132px;
    max-height: 132px;
    width: auto;
    height: auto;
  }
`;

const StyledLabel = styled.span`
  display: flex;
  align-items: center;
  font-size: var(--xsmall-font-size);
  line-height: var(--xsmall-line-height);
  font-weight: 500;
`;

type MediaUploadProps = {
  label: string;
  description: string;
  purpose: FilePurpose;
  accept: string;
  maxFileSize: number;
  displayMode?: 'image' | 'document';
  preselectedFile?: File | null;
  onFileUpload: (file: File | null) => void;
  setIsSubmitDisabled?: (isSubmitDisabled: boolean) => void;
  isDisabled?: boolean;
  noFileText?: string;
  uploadButtonText?: string;
};

export const MediaUpload: React.FC<MediaUploadProps> = ({
  label,
  description,
  purpose,
  accept,
  maxFileSize,
  displayMode = 'document',
  preselectedFile,
  onFileUpload,
  setIsSubmitDisabled,
  isDisabled,
  noFileText,
  uploadButtonText,
}) => {
  const { t } = useTranslation();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    if (preselectedFile) {
      setSelectedFile(preselectedFile);
    }
  }, [preselectedFile]);

  const clearUpload = () => {
    setSelectedFile(null);
    onFileUpload(null);
  };

  return (
    <>
      <StyledLabel>{label}</StyledLabel>
      <FlexContainer
        gap={24}
        wrap="wrap"
        justify="flex-start"
        style={{ marginTop: 'var(--medium-big-padding)' }}
      >
        <StyledUploadWrapper>
          {selectedFile?.id && displayMode === 'image' && (
            <FlexContainer justify="space-between" className="media-img">
              <StyledThumbnail flex="1">
                <img
                  src={selectedFile?.signedUrl}
                  alt={selectedFile?.fileName}
                />
              </StyledThumbnail>
              {!isDisabled && (
                <AppButton
                  type="text"
                  severity="secondary"
                  icon="pi pi-times"
                  onClick={clearUpload}
                />
              )}
            </FlexContainer>
          )}
          {selectedFile?.id && displayMode === 'document' && (
            <FlexContainer justify="space-between" gap={12}>
              <span>{selectedFile?.fileName}</span>
              {!isDisabled && (
                <AppButton
                  type="text"
                  severity="secondary"
                  icon="pi pi-times"
                  onClick={clearUpload}
                />
              )}
            </FlexContainer>
          )}
          {!selectedFile?.id && (
            <span>{noFileText || t('account.guidelines.no.file')}</span>
          )}
        </StyledUploadWrapper>
        {!isDisabled && (
          <AppButton
            label={uploadButtonText || t('generic.upload')}
            type="outlined"
            severity="secondary"
            size="sm"
            onClick={() => setShowUploadModal(true)}
          />
        )}
      </FlexContainer>
      <MediaUploadModal
        description={description}
        visible={showUploadModal}
        onHide={() => setShowUploadModal(false)}
        onFileUpload={onFileUpload}
        setIsSubmitDisabled={setIsSubmitDisabled}
        purpose={purpose}
        accept={accept}
        maxFileSize={maxFileSize}
        selectedFile={selectedFile}
        setSelectedFile={setSelectedFile}
      />
    </>
  );
};
